import {
  ConditionalOperatorPrimitive,
  FormFieldSchemaType,
  SchemaRadioYesOrNo,
  SchemaTravellersRelationship,
  SchemaTravellersType,
  ValueFormatter,
  type FormStepSchema,
  type SelectFieldOption,
} from '@/types';
import yesOrNoOptions from '@data-shared/constants/yesOrNoOptions';
import { schemaFieldName } from '../schemaFieldName';
import { schemaStepName } from '../schemaStepName';

interface GenerateNewClaimStepWhoFormSchemaParams {
  listedTravellersOptions?: SelectFieldOption<string>[];
}

const { tripDetails, paymentDetails, alternativeClaimOption } = schemaFieldName;

let stepWhoFormSchema: FormStepSchema = {
  name: schemaStepName.stepWho,
  title: 'Who did this happen to?',
  lastUpdated: Date.now(),
  form: {
    name: `${schemaStepName.stepWho}Form`,
    fields: [],
  },
};

function generateNewClaimStepWhoFormSchema({
  listedTravellersOptions = [
    {
      // This will be shown as placeholder, when loading.
      label: 'Unknown Traveller (Retrieving details...)',
      value: 'Unknown Traveller (Retrieving details...)',
    },
  ],
}: GenerateNewClaimStepWhoFormSchemaParams = {}): FormStepSchema {
  const { form, ...otherSchemaProps } = stepWhoFormSchema;
  stepWhoFormSchema = {
    ...otherSchemaProps,
    lastUpdated: Date.now(),
    form: {
      name: form.name,
      fields: [
        {
          type: FormFieldSchemaType.Checkbox,
          name: tripDetails.impactedTravellers.listedTravellers,
          label: 'Select all travellers listed on your policy affected by this incident.',
          options: listedTravellersOptions,
        },
        {
          type: FormFieldSchemaType.Radio,
          name: tripDetails.travellersType,
          label: 'Were others not listed on your policy affected by this incident.',
          layoutOptions: {
            hasSeparator: true,
          },
          options: [
            {
              label: 'No',
              value: SchemaTravellersType.Listed,
            },
            {
              label: 'Yes',
              value: SchemaTravellersType.NotListed,
            },
          ],
        },
        {
          type: FormFieldSchemaType.Text,
          name: tripDetails.impactedTravellers.unlistedTravellers.firstName,
          label: 'First name',
          placeholder: 'Enter first name',
          isVisibleWhen: [
            {
              field: tripDetails.travellersType,
              value: SchemaTravellersType.NotListed,
              operator: ConditionalOperatorPrimitive.Equal,
            },
          ],
        },
        {
          type: FormFieldSchemaType.Text,
          name: tripDetails.impactedTravellers.unlistedTravellers.lastName,
          label: 'Last name',
          placeholder: 'Enter last name',
          isVisibleWhen: [
            {
              field: tripDetails.travellersType,
              value: SchemaTravellersType.NotListed,
              operator: ConditionalOperatorPrimitive.Equal,
            },
          ],
        },
        {
          type: FormFieldSchemaType.Select,
          name: tripDetails.impactedTravellers.unlistedTravellers.relationshipToPolicyHolder,
          label: 'Relationship to policyholder',
          placeholder: 'Select relationship',
          isVisibleWhen: [
            {
              field: tripDetails.travellersType,
              value: SchemaTravellersType.NotListed,
              operator: ConditionalOperatorPrimitive.Equal,
            },
          ],
          options: [
            {
              label: 'Business partner',
              value: SchemaTravellersRelationship.BusinessPartner,
            },
            {
              label: 'Friend',
              value: SchemaTravellersRelationship.Friend,
            },
            {
              label: 'Relative',
              value: SchemaTravellersRelationship.Relative,
            },
            {
              label: 'Travel companion',
              value: SchemaTravellersRelationship.TravelCompanion,
            },
            {
              label: 'Other',
              value: SchemaTravellersRelationship.Other,
            },
          ],
        },
        {
          type: FormFieldSchemaType.Radio,
          name: alternativeClaimOption.otherClaimSource,
          label: 'Can you make a claim through any other source or insurance policy?',
          description:
            '(e.g. private health insurance, transport provider, or another third party.)',
          options: yesOrNoOptions,
          layoutOptions: {
            hasSeparator: true,
            optionsPositioning: 'inline',
          },
        },
        {
          type: FormFieldSchemaType.Textarea,
          name: alternativeClaimOption.otherClaimDescription,
          label:
            "Tell us about the claim(s) you've submitted or will submit through other sources.",
          description:
            'Please include information on the source, the policy, and the current claim status.',
          placeholder: 'You can enter up to 250 characters.',
          maxLength: 250,
          isVisibleWhen: [
            {
              field: alternativeClaimOption.otherClaimSource,
              value: SchemaRadioYesOrNo.Yes,
              operator: ConditionalOperatorPrimitive.Equal,
            },
          ],
        },
        {
          type: FormFieldSchemaType.Radio,
          name: paymentDetails.inputTaxCreditEntitlement,
          label:
            'Have you claimed, or are you able to claim back, the GST Input Tax Credit (ITC) for the insurance policy related to this claim?',
          description:
            'You may be able to claim the GST if you were travelling for business reasons.',
          options: yesOrNoOptions,
          layoutOptions: {
            hasSeparator: true,
            optionsPositioning: 'inline',
          },
        },
        {
          type: FormFieldSchemaType.Number,
          name: paymentDetails.gstPercent,
          label: 'Enter the GST amount',
          description: "Enter the percentage of GST you've claimed or are able to claim.",
          placeholder: '%',
          layoutOptions: {
            width: '1/2-block',
          },
          isVisibleWhen: [
            {
              field: paymentDetails.inputTaxCreditEntitlement,
              value: SchemaRadioYesOrNo.Yes,
              operator: ConditionalOperatorPrimitive.Equal,
            },
          ],
          valueFormatter: ValueFormatter.Percentage,
        },
        {
          type: FormFieldSchemaType.Tel,
          name: paymentDetails.abn,
          label: 'Enter your Australian Business Number (ABN).',
          placeholder: '12345678910',
          layoutOptions: {
            width: '1/2-block',
          },
          isVisibleWhen: [
            {
              field: paymentDetails.inputTaxCreditEntitlement,
              value: SchemaRadioYesOrNo.Yes,
              operator: ConditionalOperatorPrimitive.Equal,
            },
          ],
        },
      ],
    },
  };

  return stepWhoFormSchema;
}

function getNewClaimStepWhoFormSchema() {
  if (!stepWhoFormSchema.form.fields.length) {
    // Generate a new schema with all fields, if its empty!
    stepWhoFormSchema = generateNewClaimStepWhoFormSchema();
  }

  return stepWhoFormSchema;
}

export { generateNewClaimStepWhoFormSchema, getNewClaimStepWhoFormSchema };

export type { GenerateNewClaimStepWhoFormSchemaParams };
